/**
 * 🚚 SPACE DELIVERY - Module Missions (Livraisons)
 * Gestion des livraisons style cockpit spatial
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Truck, Package, Clock, CheckCircle, XCircle, MapPin,
  Phone, User, Calendar, Filter, RefreshCw, Navigation,
  Loader2, ChevronDown, AlertTriangle, Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../services/api';
import { toast } from 'sonner';

// Statuts de livraison avec couleurs
const DELIVERY_STATUS = {
  pending: { label: 'En attente', color: 'yellow', icon: Clock },
  confirmed: { label: 'Confirmée', color: 'cyan', icon: CheckCircle },
  preparing: { label: 'Préparation', color: 'orange', icon: Package },
  delivering: { label: 'En cours', color: 'blue', icon: Truck },
  delivered: { label: 'Livrée', color: 'green', icon: CheckCircle },
  cancelled: { label: 'Annulée', color: 'red', icon: XCircle },
};

// Carte de livraison
const DeliveryCard = ({ delivery, onUpdateStatus, onCall }) => {
  const [expanded, setExpanded] = useState(false);
  const status = DELIVERY_STATUS[delivery.status] || DELIVERY_STATUS.pending;
  const StatusIcon = status.icon;

  // Extraire les données (compatibilité avec les deux formats)
  const clientName = delivery.prenom && delivery.nom 
    ? `${delivery.prenom} ${delivery.nom}` 
    : delivery.customerName || 'Client';
  const clientPhone = delivery.telephone || delivery.customerPhone || '';
  const clientAddress = delivery.adresse || delivery.address || '';
  const orderDetails = delivery.commentaire || delivery.orderDetails || '';
  
  // Extraire le créneau du commentaire si présent
  const slotMatch = orderDetails.match(/🕐 Créneau: ([^\n]+)/);
  const timeSlot = slotMatch ? slotMatch[1] : (delivery.requestedTime || 'ASAP');

  const colorClasses = {
    yellow: 'border-yellow-500/30 bg-yellow-500/10',
    cyan: 'border-cyan-500/30 bg-cyan-500/10',
    orange: 'border-orange-500/30 bg-orange-500/10',
    blue: 'border-blue-500/30 bg-blue-500/10',
    green: 'border-green-500/30 bg-green-500/10',
    red: 'border-red-500/30 bg-red-500/10',
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`backdrop-blur-xl rounded-2xl border ${colorClasses[status.color]} overflow-hidden`}
    >
      <div 
        className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          {/* Statut */}
          <div className={`p-3 rounded-xl bg-${status.color}-500/20 border border-${status.color}-500/30`}>
            <StatusIcon className={`w-6 h-6 text-${status.color}-400`} />
          </div>

          {/* Infos principales */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-white font-bold">{clientName}</h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold bg-${status.color}-500/20 text-${status.color}-400`}>
                {status.label}
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {clientAddress ? clientAddress.substring(0, 35) + '...' : 'Adresse non définie'}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {timeSlot}
              </span>
            </div>
          </div>

          {/* Actions rapides */}
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onCall?.(clientPhone); }}
              className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-colors"
              title="Appeler"
            >
              <Phone className="w-4 h-4" />
            </button>
            <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </div>

      {/* Détails expandés */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-white/10 overflow-hidden"
          >
            <div className="p-4 space-y-4">
              {/* Détails commande */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 text-xs mb-1">📍 Adresse complète</p>
                  <p className="text-white">{clientAddress || 'Non définie'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">📞 Téléphone</p>
                  <p className="text-cyan-400 font-mono text-lg">{clientPhone || 'Non défini'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500 text-xs mb-1">🛒 Commande</p>
                  <p className="text-white whitespace-pre-line">{orderDetails || 'Voir détails'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">🕐 Créée le</p>
                  <p className="text-gray-400">{new Date(delivery.createdAt).toLocaleString('fr-FR')}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">🚚 Zone</p>
                  <p className="text-cyan-400">{delivery.zoneLabel || delivery.zone || 'Non définie'}</p>
                </div>
              </div>

              {/* Boutons de changement de statut */}
              <div className="flex flex-wrap gap-2">
                {delivery.status === 'pending' && (
                  <>
                    <Button
                      size="sm"
                      onClick={() => onUpdateStatus(delivery.id, 'confirmed')}
                      className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" /> Confirmer
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => onUpdateStatus(delivery.id, 'cancelled')}
                      className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Annuler
                    </Button>
                  </>
                )}
                {delivery.status === 'confirmed' && (
                  <Button
                    size="sm"
                    onClick={() => onUpdateStatus(delivery.id, 'preparing')}
                    className="bg-orange-500/20 text-orange-400 hover:bg-orange-500/30"
                  >
                    <Package className="w-4 h-4 mr-1" /> Préparer
                  </Button>
                )}
                {delivery.status === 'preparing' && (
                  <Button
                    size="sm"
                    onClick={() => onUpdateStatus(delivery.id, 'delivering')}
                    className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                  >
                    <Truck className="w-4 h-4 mr-1" /> Partir en livraison
                  </Button>
                )}
                {delivery.status === 'delivering' && (
                  <Button
                    size="sm"
                    onClick={() => onUpdateStatus(delivery.id, 'delivered')}
                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Marquer livrée
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="border-cyan-600 text-cyan-400 hover:bg-cyan-500/20"
                  onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(clientAddress)}`, '_blank')}
                >
                  <Navigation className="w-4 h-4 mr-1" /> GPS
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Module Livraisons principal
const SpaceDelivery = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  // Charger les livraisons
  const loadDeliveries = async () => {
    setIsLoading(true);
    try {
      const data = await api.getDeliveryRequests() || [];
      setDeliveries(Array.isArray(data) ? data : data.deliveries || data.requests || []);
    } catch (error) {
      console.error('Erreur chargement livraisons:', error);
      setDeliveries([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDeliveries();
  }, []);

  // Mettre à jour le statut
  const handleUpdateStatus = async (deliveryId, newStatus) => {
    try {
      await api.updateDeliveryRequest(deliveryId, { status: newStatus });
      toast.success(`🚀 Statut mis à jour : ${DELIVERY_STATUS[newStatus].label}`);
      loadDeliveries();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Appeler un client
  const handleCall = (phone) => {
    if (phone) {
      window.open(`tel:${phone}`, '_self');
    }
  };

  // Filtrer les livraisons
  const filteredDeliveries = filter === 'all' 
    ? deliveries 
    : deliveries.filter(d => d.status === filter);

  // Compter par statut
  const counts = {
    all: deliveries.length,
    pending: deliveries.filter(d => d.status === 'pending').length,
    confirmed: deliveries.filter(d => d.status === 'confirmed').length,
    delivering: deliveries.filter(d => d.status === 'delivering').length,
    delivered: deliveries.filter(d => d.status === 'delivered').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-500">
            🚚 MISSIONS
          </h1>
          <p className="text-gray-500 text-sm">Centre de commande des livraisons</p>
        </div>
        <Button
          onClick={loadDeliveries}
          className="bg-orange-500/20 text-orange-400 hover:bg-orange-500/30"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { key: 'all', label: 'Toutes', color: 'gray' },
          { key: 'pending', label: 'En attente', color: 'yellow' },
          { key: 'confirmed', label: 'Confirmées', color: 'cyan' },
          { key: 'delivering', label: 'En cours', color: 'blue' },
          { key: 'delivered', label: 'Livrées', color: 'green' },
        ].map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`p-3 rounded-xl text-center transition-all ${
              filter === key 
                ? `bg-${color}-500/20 border-2 border-${color}-500` 
                : `bg-[#1a1a25] border border-gray-700 hover:border-${color}-500/50`
            }`}
          >
            <p className={`text-2xl font-bold text-${color}-400`}>{counts[key]}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </button>
        ))}
      </div>

      {/* Liste des livraisons */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
        </div>
      ) : filteredDeliveries.length === 0 ? (
        <div className="text-center py-12 backdrop-blur-xl rounded-2xl border border-gray-700 bg-[#12121a]/60">
          <Truck className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">Aucune livraison {filter !== 'all' ? `"${DELIVERY_STATUS[filter]?.label}"` : ''}</p>
          <p className="text-gray-600 text-sm mt-2">Les nouvelles commandes apparaîtront ici</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredDeliveries.map((delivery) => (
            <DeliveryCard
              key={delivery.id}
              delivery={delivery}
              onUpdateStatus={handleUpdateStatus}
              onCall={handleCall}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SpaceDelivery;
